/* eslint-disable  @typescript-eslint/no-explicit-any */
import type { AppDispatch } from '@nextgen-web-framework/core';
import { AnyAction, AsyncThunk, createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { InitialSubscriptionState } from '../../types/subscriptionTypes';
import type { Subscriptions, SubscriptionResponse, Profiles, ProfileResponse } from '../models';
import { subscriptionApi } from '../requests';

type GenericAsyncThunk = AsyncThunk<
  SubscriptionResponse,
  unknown,
  {
    // Optional fields for defining thunkApi field types
    dispatch: AppDispatch;
    state: InitialSubscriptionState;
    extra: {
      subscriptionBaseApiUrl: string;
    };
  }
>;
type PendingAction = ReturnType<GenericAsyncThunk['pending']>;
type RejectedAction = ReturnType<GenericAsyncThunk['rejected']>;
type FulfilledAction = ReturnType<GenericAsyncThunk['fulfilled']>;

function isPendingGenericAction(action: AnyAction): action is PendingAction {
  return action.type.endsWith('/pending');
}
function isFulfilledAction(action: AnyAction): action is FulfilledAction {
  return action.type.endsWith('/fulfilled');
}
function isRejectedAction(action: AnyAction): action is RejectedAction {
  return action.type.endsWith('/rejected');
}

export const getAllSubscriptionSliceReq = createAsyncThunk<
  Subscriptions,
  {
    accountId?: string;
  },
  {
    dispatch: any;
    state: InitialSubscriptionState;
    extra: {
      subscriptionBaseApiUrl: string;
    };
  }
>('commerceSubscription/getAllSubscriptions', async (payload) => {
  const res: Subscriptions = await subscriptionApi.getAllSubscriptions({ ...payload });
  return res;
});

export const getSubscriptionDetailsSliceReq = createAsyncThunk<
  SubscriptionResponse,
  {
    subscriptionId: string;
  },
  {
    dispatch: any;
    state: InitialSubscriptionState;
    extra: {
      subscriptionBaseApiUrl: string;
    };
  }
>('commerceSubscription/getSubscriptionDetails', async (payload) => {
  const res: SubscriptionResponse = await subscriptionApi.getSubscriptionById({
    ...payload,
  });

  return res;
});

export const getAllProfilesSliceReq = createAsyncThunk<
  Profiles,
  {
    accountId: string;
    userProfileId?: string;
    page?: string;
    limit?: string;
  },
  {
    dispatch: any;
    state: InitialSubscriptionState;
    extra: {
      subscriptionBaseApiUrl: string;
    };
  }
>('commerceSubscription/getAllProfiles', async (payload) => {
  const res: Profiles = await subscriptionApi.getSubscriptionProfileList({
    ...payload,
  });

  return res;
});

export const getProfiledetailsSliceReq = createAsyncThunk<
  ProfileResponse,
  {
    profileId: string;
  },
  {
    dispatch: any;
    state: InitialSubscriptionState;
    extra: {
      subscriptionBaseApiUrl: string;
    };
  }
>('commerceSubscription/getProfiledetails', async (payload) => {
  const res: ProfileResponse = await subscriptionApi.getSubscriptionProfile({
    ...payload,
  });

  return res;
});

const initialState: InitialSubscriptionState = {
  isLoading: false,
  hasError: false,
  error: {},
  selectedSubscriptionPlan: {
    planId: '',
    plan: {},
    paymentDate: '',
    cycle: {},
    tncAccepted: false,
  },
  profile: {},
  currentView: '',
  tncAccepted: false,
  subscriptionDetails: {},
  subscriptions: {},
  // profiles: [],
  profilesRes: {
    isLoading: true,
  },
  profileDetails: {},
  plans: [],
  planDetails: {},
  bundleType: '',
  showGcoConfirmationModal: false,
  subscriptionsCmsData: [],
  subscriptionPlanCMSId: '',
  products: {},
  isProductsEdited: false,
};

export const subscriptionSlice = createSlice({
  name: 'commerceSubscription',
  initialState,
  reducers: {
    resetSubscriptionSlice: () => initialState,
    updateSelectedSubscription: (state, action) => {
      state.selectedSubscriptionPlan = action.payload;
    },
    updateProfile: (state, action) => {
      state.profile = action.payload;
    },
    updateCurrentView: (state, action) => {
      state.currentView = action.payload;
    },
    updateTncAccepted: (state, action) => {
      state.tncAccepted = action.payload;
    },
    updateSubscriptionDetails: (state, action) => {
      state.subscriptionDetails = action.payload;
    },
    updateProfileDetails: (state, action) => {
      state.profileDetails = action.payload;
    },
    updatePlans: (state, action) => {
      state.plans = action.payload;
    },
    updateBundleState: (state, action) => {
      state.bundleState = action.payload;
    },
    updateProductType: (state, action) => {
      state.productType = action.payload;
    },
    updateProductId: (state, action) => {
      state.productId = action.payload;
    },
    updatePlanDetails: (state, action) => {
      state.planDetails = action.payload;
    },
    updateBundleType: (state, action) => {
      state.bundleType = action.payload;
    },
    updateEnableSubscription: (state, action) => {
      state.enableSubscription = action.payload;
    },
    setSubscriptionCheckoutErrorState: (state, action) => {
      state.hasError = action.payload;
    },
    clearSubscriptions: (state) => {
      state.subscriptions = {};
    },
    updateShowGcoConfirmationModal: (state, action) => {
      state.showGcoConfirmationModal = action.payload;
    },
    updateSubscriptionsCmsData: (state, action) => {
      state.subscriptionsCmsData = action.payload;
    },
    updateCartDetails: (state, action) => {
      state.cartDetails = action.payload;
    },
    updateSubscriptionPlanCMSId: (state, action) => {
      state.subscriptionPlanCMSId = action.payload;
    },
    updatePlanOrderBenefitSelections: (state, action) => {
      state.planOrderBenefitSelections = action.payload;
    },
    updateProducts: (state, action) => {
      state.products = action.payload;
    },
    clearAllProfiles: (state) => {
      state.profilesRes = {
        isLoading: true,
      };
    },
    updateIsProductsEdited: (state, action) => {
      state.isProductsEdited = action.payload;
    },
  },
  extraReducers: (builder) => {
    /**
     * update all Subscriptions
     */
    builder.addCase(getAllSubscriptionSliceReq.fulfilled, (state, action) => {
      state.isLoading = false;
      state.hasError = false;

      state.subscriptions = {
        metadata: action.payload.metadata,
        subscriptions: (state.subscriptions?.subscriptions ?? []).concat(action.payload.subscriptions ?? []),
      } as Subscriptions;
    });
    /**
     * update Subscription Details
     */
    builder.addCase(getSubscriptionDetailsSliceReq.fulfilled, (state, action) => {
      state.isLoading = false;
      state.hasError = false;
      state.subscriptionDetails = action.payload;
    });
    /**
     * update all Profiles
     */
    builder.addCase(getAllProfilesSliceReq.fulfilled, (state, action) => {
      // state.profiles = (state.profiles ?? []).concat(action.payload.profiles ?? []);
      state.profilesRes = {
        metadata: action.payload.metadata,
        profiles: (state.profilesRes?.profiles ?? []).concat(action.payload.profiles ?? []),
        isLoading: false,
      } as Profiles;
    });
    builder.addCase(getAllProfilesSliceReq.rejected, (state) => {
      state.profilesRes = {
        ...state.profilesRes,
        isLoading: false,
      } as Profiles;
    });
    /**
     * update Profile Details
     */
    builder.addCase(getProfiledetailsSliceReq.fulfilled, (state, action) => {
      state.isLoading = false;
      state.hasError = false;
      state.profileDetails = action.payload;
    });

    /**
     * Common reducers
     */
    builder.addMatcher(isRejectedAction, (state: InitialSubscriptionState, action: any) => {
      state.isLoading = false;
      state.hasError = true;
      state.error = action.payload;
    });
    builder.addMatcher(isFulfilledAction, (state: InitialSubscriptionState) => {
      state.isLoading = false;
      state.hasError = false;
    });
    builder.addMatcher(isPendingGenericAction, (state: InitialSubscriptionState) => {
      state.isLoading = true;
      state.hasError = false;
    });
  },
});

export const {
  resetSubscriptionSlice,
  updateSelectedSubscription,
  updateProfile,
  updateCurrentView,
  updateTncAccepted,
  updateSubscriptionDetails,
  updateProfileDetails,
  updatePlanDetails,
  updatePlans,
  updateBundleState,
  updateProductType,
  updateProductId,
  updateBundleType,
  updateEnableSubscription,
  setSubscriptionCheckoutErrorState,
  clearSubscriptions,
  updateShowGcoConfirmationModal,
  updateSubscriptionsCmsData,
  updateCartDetails,
  updateSubscriptionPlanCMSId,
  updatePlanOrderBenefitSelections,
  updateProducts,
  clearAllProfiles,
  updateIsProductsEdited,
} = subscriptionSlice.actions;
